import { useEffect, useId } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { requiredText } from '@/lib/validators'
import { Button } from '@/ui/button'
import { FormField } from '@/ui/form-field'

import {
  createEntityErrorMessage,
  useCreateEntity,
} from '../../lib/entity-queries'

const createEntitySchema = z.object({
  name: requiredText.transform((value) => value.trim()),
})

type CreateEntityValues = z.infer<typeof createEntitySchema>

export function CreateEntityForm({
  parentId,
  parentName,
  onCancel,
  onSuccess,
}: {
  parentId: string
  parentName: string
  onCancel: () => void
  onSuccess: () => void
}) {
  const requestErrorId = useId()
  const create = useCreateEntity()
  const { control, handleSubmit, setFocus } = useForm<CreateEntityValues>({
    resolver: zodResolver(createEntitySchema),
    defaultValues: { name: '' },
    mode: 'onSubmit',
  })

  useEffect(() => setFocus('name'), [setFocus])

  function submit({ name }: CreateEntityValues) {
    create.mutate({ name, parentEntityId: parentId }, { onSuccess })
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        create.reset()
        void handleSubmit(submit)(event)
      }}
      className="flex flex-col gap-3"
    >
      <div className="flex flex-col gap-1.5">
        <p className="text-xs font-medium">New entity</p>
        <p className="text-muted-foreground text-xs">
          This new entity will become a child of{' '}
          <strong className="text-foreground font-semibold">
            {parentName}
          </strong>
          .
        </p>
        <FormField
          control={control}
          name="name"
          spec={{ label: 'Entity name', placeholder: 'Entity name' }}
          disabled={create.isPending}
          describedBy={create.isError ? requestErrorId : undefined}
          onKeyDown={(event) => {
            if (event.key !== 'Escape') return
            event.preventDefault()
            event.stopPropagation()
            onCancel()
          }}
        />
        {create.isError && (
          <p
            id={requestErrorId}
            role="alert"
            className="text-destructive text-xs"
          >
            {createEntityErrorMessage(create.error)}
          </p>
        )}
      </div>
      <div className="flex justify-end gap-1.5">
        <Button
          type="button"
          variant="ghost"
          disabled={create.isPending}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={create.isPending}>
          {create.isPending ? 'Creating…' : 'Create'}
        </Button>
      </div>
    </form>
  )
}
